import { RxLifespanDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from 'react';
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

const useRxLifespanDistributionStats = (startDate: Date, endDate: Date, locationIds?: string[], rxType?: string) => {
  const BIStore = useStore(BIContext); 
  const { getRxLifespanStats } = BIStore; 

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allRxLifespan, setAllRxLifespan] = useState<RxLifespanDto[]>([]);
  // Store filtered stats per selected locations
  const [rxLifespan, setRxLifespan] = useState<RxLifespanDto[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: RxLifespanDto[] | undefined;
        if (debounceStartDate && debounceEndDate) {
          // console.log(`[Rx Lifespan Distribution] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          // Always fetch all locations data, don't pass location filter to backend
          response = await getRxLifespanStats(debounceStartDate, debounceEndDate, undefined, rxType);
          // console.log("[Rx Lifespan Distribution] Backend responded with:", response);
        } else {
          // console.log("[Rx Lifespan Distribution] Fetching all data");
        }
        
        if (response && response.length > 0) {
          setAllRxLifespan(response);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setRxLifespan(response.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setRxLifespan(response);
          }
        } else {
          // console.log("[Rx Lifespan Distribution] No data found for the specified range.");
          setAllRxLifespan([]);
          setRxLifespan([]);
        }
      } catch (error) {
        // console.log("[Rx Lifespan Distribution] Error fetching Rx stats: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debounceStartDate, debounceEndDate, getRxLifespanStats, rxType]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allRxLifespan.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setRxLifespan(allRxLifespan.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setRxLifespan(allRxLifespan);
      }
    }
  }, [memoizedLocationIds, allRxLifespan, isLoading]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allRxLifespan.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allRxLifespan]);

  return { rxLifespan, isLoading, availableLocations };
};

export default useRxLifespanDistributionStats;
